import React, { JSX, useState } from "react";
import { useSelector } from 'react-redux';
import { RootState, BasePageProps, ButtonStylized, ButtonStylizedType, ButtonShare } from '@mental-mint/shared-ui';
import { motion } from "framer-motion";
import * as Lucide from "lucide-react";
import { GameState, getShareText } from "../../WordDriftUtils";
import type { GameWord } from "../../Utils/GameTypes";
import { logger } from '@mental-mint/shared-utils';
import { ClickableWord } from '../../Components/WordDisplay/WordDisplay';
import "./PagePostGame.css";

/**
 * Props for the WordDrift PagePostGame component.
 */
interface PagePostGameProps extends BasePageProps {
  /** Game state from completed session */
  gameState?: GameState;
  /** Callback to open puzzle archive */
  onArchiveOpen?: () => void;
}

/**
 * Post-game results page for WordDrift.
 * Shows game path and provides options to play again or share results.
 */
export const PagePostGame: React.FC<PagePostGameProps> = ({
  onRequestClose,
  gameState,
  onArchiveOpen,
}) => {
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  const game = currentGame!;
  const [arePathsExpanded, setArePathsExpanded] = useState(false);
  const [isOptimalPathRevealed, setIsOptimalPathRevealed] = useState(false);

  if (!gameState) {
    return <div>Error: No game state available</div>;
  }

  const renderHeader = (): JSX.Element => {
    const isWinner = gameState.isWon;
    const headerText = isWinner ? `Well done!` : "Time's up!";
    const headerColor = game.colorPrimary;

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{
          textAlign: "center",
          marginBottom: "1rem"
        }}
      >
        <div style={{
          fontFamily: game.fontSecondary,
          fontSize: "2rem",
          fontWeight: "bold",
          color: headerColor,
          margin: "0 0 0.5rem 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.75rem"
        }}>
          {isWinner 
            ? <Lucide.AlarmClockCheck size={30} strokeWidth={3} /> 
            : <Lucide.AlarmClockOff size={30} strokeWidth={3} />
          }
          {headerText}
        </div>
      </motion.div>
    );
  };

  const renderOptimalPathInfo = (): JSX.Element | null => {
    // Extract string paths from GameWord arrays
    // Handle both proper GameWord format and reconstructed format from save data
    const userPathStrings = gameState.path
      .map((gw: any) => gw?.variations?.display || gw?.display || gw?.variations?.lemmatized || gw?.lemmatized)
      .filter(Boolean) as string[];
    
    // Extract optimal path strings - handle both formats
    const optimalPathStrings = gameState.optimalPath
      ?.map((gw: any) => gw?.variations?.display || gw?.display || gw?.variations?.lemmatized || gw?.lemmatized)
      .filter(Boolean) as string[];
    
    if (!optimalPathStrings || optimalPathStrings.length === 0) {
      logger.log("No optimal path available");
      return null;
    }

    const optimalMoves = optimalPathStrings.length - 1;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        style={{
          textAlign: "center",
          borderRadius: "12px",
          marginBottom: "1rem",
        }}
      >
        <div 
          className="path-info"
          style={{
            fontFamily: game.fontSecondary,
            fontSize: "1rem",
            color: "rgba(0, 0, 0, 0.8)",
            marginBottom: "0.5rem",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxSizing: "border-box",
            width: "100%",
            borderRadius: "8px",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            border: "2px solid rgba(255, 255, 255, 0.1)",
            boxShadow: `0 0 5px ${game.colorPrimary}`,
            animation: arePathsExpanded ? "none" : "pulse-border 2s ease-in-out infinite",
          }}
          onClick={() => setArePathsExpanded(!arePathsExpanded)}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
            <div style={{padding: "1rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem"}}>
              <div>Your path: <strong style={{ color: game.colorPrimary }}>
                {gameState.isWon ? `${userPathStrings.length - 1} moves` : "incomplete"}
              </strong></div>
          
              {arePathsExpanded && renderPath(
                userPathStrings, 
                (gameState.pathLength || userPathStrings.length) > userPathStrings.length
              )}

              <div style={{
                marginTop: "0.5rem",
                marginBottom: "0.5rem",
                borderTop: "1px solid rgba(0, 0, 0, 0.2)",
                width: "100%"
              }}/>

              <div>Sample path: <strong style={{ color: game.colorPrimary }}>{optimalMoves} moves</strong></div> 

              {arePathsExpanded && (
                <div style={{ position: "relative", marginTop: "0.5rem" }}>
                  <div style={{
                    filter: isOptimalPathRevealed ? "none" : "blur(8px)",
                    transition: "filter 0.3s ease"
                  }}>
                    {renderPath(optimalPathStrings)}
                  </div>
                  {!isOptimalPathRevealed && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 10
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ButtonStylized 
                        buttonType={ButtonStylizedType.Primary}
                        onClick={() => setIsOptimalPathRevealed(true)}
                        text="Reveal"
                        additionalStyles={{ 
                          fontSize: "1.1rem", 
                          padding: "0rem 1.3rem",
                          height: "55px",
                          // border: `2px solid rgba(0, 0, 0, 0.7)`,
                          borderRadius: "40px",
                          color: "rgba(0, 0, 0, 0.8)",
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{
              backgroundColor: "rgba(0, 0, 0, 0.1)", width: "100%", 
              borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px", 
              display: "flex", justifyContent: "center", alignItems: "center"
            }}>
              {arePathsExpanded ? (
                <Lucide.ChevronUp size={22} color="rgba(0, 0, 0, 0.6)" />
              ) : (
                <Lucide.ChevronDown size={22} color="rgba(0, 0, 0, 0.6)" />
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderAveragePathInfo = (): JSX.Element | null => {
    // Extract user path strings
    const userPathStrings = gameState.path
      .map((gw: any) => gw?.variations?.display || gw?.display || gw?.variations?.lemmatized || gw?.lemmatized)
      .filter(Boolean) as string[];
    
    // Extract optimal path strings - handle both formats
    const optimalPathStrings = gameState.optimalPath
      ?.map((gw: any) => gw?.variations?.display || gw?.display || gw?.variations?.lemmatized || gw?.lemmatized)
      .filter(Boolean) as string[];
    
    if (!optimalPathStrings || optimalPathStrings.length === 0) {
      return null;
    }

    const userMoves = userPathStrings.length - 1;
    const optimalMoves = optimalPathStrings.length - 1;
    // Stub: Calculate average as 1.3x optimal path length
    // TODO: Replace with actual backend data when available
    const averageUserMoves = optimalMoves * 1.3;
    
    // Different text based on whether user won
    let displayText: JSX.Element;
    let emoji: string;
    
    if (gameState.isWon) {
      // Determine comparison text and emoji
      let comparisonText: string;
      const difference = Math.abs(userMoves - averageUserMoves);
      if (difference < 0.5) {
        comparisonText = "close to";
        emoji = "👍";
      } else if (userMoves < averageUserMoves) {
        comparisonText = "faster than";
        emoji = "🎉";
      } else {
        comparisonText = "slower than";
        emoji = "🤔";
      }
      
      displayText = (
        <>
          {emoji} You solved this puzzle in <strong>{userMoves} {userMoves === 1 ? 'move' : 'moves'}</strong>, which is <strong>{comparisonText}</strong> the community average of <strong>{averageUserMoves.toFixed(1)} moves</strong> (across all players).
        </>
      );
    } else {
      emoji = "📊";
      displayText = (
        <>
          {emoji} The community average for this puzzle is <strong>{averageUserMoves.toFixed(1)} moves</strong> (across all players).
        </>
      );
    }
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        style={{
          marginBottom: "2rem",
          fontFamily: game.fontSecondary,
          fontSize: "1rem",
          width: "100%",
          maxWidth: "500px",
          textAlign: "center",
          color: "rgba(0, 0, 0, 0.7)",
        }}
      >
        {displayText}
      </motion.div>
    );
  };

  const renderPath = (pathWords: string[], isTruncated: boolean = false): JSX.Element => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0 }}
        style={{
          marginTop: "0.5rem",
          textAlign: "center"
        }}
      >
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "1rem",
          margin: "0 auto"
        }}>
          {pathWords.map((word, index) => {
            const isFirst = index === 0;
            const isLast = index === pathWords.length - 1;
            // Use exact word matching for goal detection
            const isGoal = word.toLowerCase() === gameState.goalWord.toLowerCase() && isLast;
            return (
              <div key={`${word}-${index}`} style={{ position: "relative" }}>
                <ClickableWord
                  word={word}
                  gameColor={game.colorPrimary}
                  onClick={() => {}} // No-op in post-game display
                  keyId={`path-${index}`}
                  highlightWord={isGoal}
                  isGrayedOut={false}
                />
                {index < pathWords.length - 1 && (
                  <Lucide.ChevronRight 
                    style={{
                      position: "absolute",
                      right: "-1rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "rgba(0, 0, 0, 0.6)"
                    }}
                    size={16}
                  />
                )}
              </div>
            );
          })}
          {isTruncated && (
            <div key="truncation-indicator" style={{ position: "relative" }}>
              <div style={{
                fontFamily: game.fontSecondary,
                fontSize: "1.1rem",
                color: "rgba(0, 0, 0, 0.5)",
                padding: "0.5rem 1rem",
                fontWeight: "bold"
              }}>
                ...
              </div>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const renderActions = (): JSX.Element => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          alignItems: "center",
          maxWidth: "400px",
          margin: "0 auto"
        }}
      >
        <div style={{ display: "flex", gap: "0.8rem", flexWrap: "wrap", justifyContent: "center", width: "100%" }}>
          <ButtonShare 
            text={getShareText(gameState, game.metadata.gameName)}
            buttonType={ButtonStylizedType.Secondary}
            additionalStyles={{ fontSize: "1.4rem", flex: "1", minWidth: "150px" }}
          />
          {onArchiveOpen && (
            <ButtonStylized 
              buttonType={ButtonStylizedType.Primary}
              onClick={onArchiveOpen}
              icon={<Lucide.Play size={20} strokeWidth={3} />}
              text={"Play more"}
              additionalStyles={{ fontSize: "1.4rem", flex: "1", minWidth: "150px" }}
            />
          )}
        </div>
      </motion.div>
    );
  };

  const renderCopyright = (): JSX.Element => {
    return (
      <p style={{ 
        fontSize: "1rem", 
        fontFamily: game.fontSecondary, 
        color: "rgba(0, 0, 0, 0.4)", 
        marginTop: "2.5rem",
        textAlign: "center"
      }}>
        &copy; {new Date().getFullYear()} Mental Mint LLC. All rights reserved.
      </p>
    );
  };

  return (
    <div className="word-drift-post-game-container" style={{
      display: "flex", 
      flexDirection: "column", 
      alignItems: "center",
      padding: "2rem",
      maxWidth: "800px",
      margin: "0 auto"
    }}>
      {renderHeader()}
      {renderAveragePathInfo()}
      {renderOptimalPathInfo()}
      {renderActions()}
      {renderCopyright()}
    </div>
  );
};

export default PagePostGame;
